import { Lightning, Fire, CalendarCheck, Trophy } from "@phosphor-icons/react";

export function ClientProgressDashboard({ phases, workouts, logs, programs }) {
  const activePhase = phases.find((p) => p.status === "in_corso");
  let phaseProgress = null;
  if (activePhase?.start_date && activePhase?.end_date) {
    const start = new Date(activePhase.start_date);
    const end = new Date(activePhase.end_date);
    const today = new Date();
    const totalDays = Math.max((end - start) / 86400000, 1);
    const elapsed = Math.max(Math.min((today - start) / 86400000, totalDays), 0);
    const pct = Math.round((elapsed / totalDays) * 100);
    const totalWeeks = Math.ceil(totalDays / 7);
    const currentWeek = Math.min(Math.ceil(elapsed / 7), totalWeeks);
    phaseProgress = { pct, currentWeek, totalWeeks, title: activePhase.title };
  }

  const today = new Date().toISOString().split("T")[0];
  const todayLogs = logs.filter((l) => l.date === today);
  const todayExerciseNames = new Set(todayLogs.map((l) => l.exercise_name));
  const dayNames = ["Domenica", "Lunedi", "Martedi", "Mercoledi", "Giovedi", "Venerdi", "Sabato"];
  const todayDayName = dayNames[new Date().getDay()];
  const todayWorkouts = workouts.filter((w) => w.day_of_week === todayDayName);
  const totalExToday = todayWorkouts.reduce((sum, w) => sum + (w.exercises?.length || 0), 0);
  const doneExToday = totalExToday > 0
    ? todayWorkouts.reduce((sum, w) => sum + (w.exercises?.filter((e) => todayExerciseNames.has(e.name)).length || 0), 0)
    : todayLogs.length;
  const todayPct = totalExToday > 0 ? Math.round((doneExToday / totalExToday) * 100) : 0;

  const lastLogDate = logs.length > 0 ? logs.reduce((latest, l) => l.date > latest ? l.date : latest, logs[0].date) : null;

  // Streak weekly: si basa SOLO sulle schede del/i programma/i con status "in_corso".
  // Non si includono i programmi "da_fare" né "completata", e nemmeno le schede
  // legate direttamente alla fase ma fuori da un programma attivo.
  const activeProgramIds = new Set(
    programs.filter((p) => p.status === "in_corso").map((p) => p.id)
  );
  const phaseWorkouts = workouts.filter((w) => activeProgramIds.has(w.program_id));
  const maxStreak = phaseWorkouts.length;

  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const weekStart = monday.toISOString().split("T")[0];
  const weekEnd = sunday.toISOString().split("T")[0];

  const thisWeekLogs = logs.filter((l) => l.date >= weekStart && l.date <= weekEnd);
  const loggedWorkoutIds = new Set(thisWeekLogs.map((l) => l.workout_id));
  const streak = phaseWorkouts.filter((w) => loggedWorkoutIds.has(w.id)).length;

  if (!phaseProgress && totalExToday === 0 && logs.length === 0) return null;

  return (
    <div className="space-y-3 mb-4" data-testid="client-progress-dashboard">
      {phaseProgress && (
        <div className="bg-[#171717] border border-[#262626] rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Lightning size={14} weight="fill" className="text-[#007AFF]" />
              <span className="text-xs font-bold text-white uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {phaseProgress.title}
              </span>
            </div>
            <span className="text-[11px] text-zinc-400">
              Settimana {phaseProgress.currentWeek} di {phaseProgress.totalWeeks}
            </span>
          </div>
          <div className="h-2 bg-[#262626] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#007AFF] to-[#34C759] transition-all duration-500"
              style={{ width: `${Math.min(phaseProgress.pct, 100)}%` }}
              data-testid="phase-progress-bar"
            />
          </div>
          <p className="text-[10px] text-zinc-600 mt-1.5 text-right">{phaseProgress.pct}% completato</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-[#171717] border border-[#262626] rounded-xl p-3 text-center">
          <CalendarCheck size={18} weight="fill" className={`mx-auto mb-1 ${todayPct >= 100 ? "text-[#34C759]" : "text-[#007AFF]"}`} />
          <p className="text-lg font-bold text-white leading-none">
            {totalExToday > 0 ? `${doneExToday}/${totalExToday}` : todayLogs.length > 0 ? todayLogs.length : "—"}
          </p>
          <p className="text-[9px] text-zinc-500 mt-1 uppercase tracking-wider">Oggi</p>
          {totalExToday > 0 && (
            <div className="h-1 bg-[#262626] rounded-full mt-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${todayPct >= 100 ? "bg-[#34C759]" : "bg-[#007AFF]"}`}
                style={{ width: `${Math.min(todayPct, 100)}%` }}
              />
            </div>
          )}
        </div>

        <div className="bg-[#171717] border border-[#262626] rounded-xl p-3 text-center">
          <Fire size={18} weight="fill" className={`mx-auto mb-1 ${streak >= maxStreak && maxStreak > 0 ? "text-[#FF9500]" : "text-zinc-500"}`} />
          <p className="text-lg font-bold text-white leading-none">
            {streak}<span className="text-zinc-600 text-xs font-normal">/{maxStreak}</span>
          </p>
          <p className="text-[9px] text-zinc-500 mt-1 uppercase tracking-wider">Streak</p>
          {maxStreak > 0 && (
            <div className="flex gap-0.5 justify-center mt-2">
              {Array.from({ length: maxStreak }, (_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full ${i < streak ? "bg-[#FF9500]" : "bg-[#262626]"}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="bg-[#171717] border border-[#262626] rounded-xl p-3 text-center">
          <Trophy size={18} weight="fill" className="mx-auto mb-1 text-[#D4FF00]" />
          <p className="text-sm font-bold text-white leading-none mt-0.5">
            {lastLogDate ? lastLogDate.slice(5).replace("-", "/") : "—"}
          </p>
          <p className="text-[9px] text-zinc-500 mt-1 uppercase tracking-wider">Ultimo log</p>
        </div>
      </div>
    </div>
  );
}
